import { useCallback, useRef, useState } from 'react';

import { cn } from '~/utils/cn';

interface Props {
  left: React.ReactNode;
  right: React.ReactNode;
  /** Starting width of the left pane, as a percentage of the container. */
  defaultLeftPct?: number;
  minLeftPct?: number;
  maxLeftPct?: number;
  /** Extra classes for the grid container. */
  className?: string;
  /** Automation hook on the grid container (default `resizable-split`); the divider gets `-divider`. */
  testId?: string;
}

/** Keyboard nudge per arrow press, in percentage points. */
const KEY_STEP = 2;

/**
 * Two-pane layout with a draggable divider. Stacks to a single column below
 * `lg` (where a horizontal split has no room), so the resize handle and the
 * percentage template only apply from that breakpoint up.
 */
export function ResizableSplit({
  left,
  right,
  defaultLeftPct = 30,
  minLeftPct = 20,
  maxLeftPct = 60,
  className,
  testId = 'resizable-split',
}: Props) {
  const [leftPct, setLeftPct] = useState(defaultLeftPct);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const clamp = useCallback(
    (pct: number) => Math.min(maxLeftPct, Math.max(minLeftPct, pct)),
    [minLeftPct, maxLeftPct],
  );

  /** Translates a pointer x-position into a clamped left-pane percentage. */
  const setFromClientX = useCallback(
    (clientX: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      setLeftPct(clamp(((clientX - rect.left) / rect.width) * 100));
    },
    [clamp],
  );

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    isDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!isDraggingRef.current) return;
    setFromClientX(e.clientX);
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    isDraggingRef.current = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowLeft') setLeftPct((p) => clamp(p - KEY_STEP));
    else if (e.key === 'ArrowRight') setLeftPct((p) => clamp(p + KEY_STEP));
    else if (e.key === 'Home') setLeftPct(minLeftPct);
    else if (e.key === 'End') setLeftPct(maxLeftPct);
    else return;
    e.preventDefault();
  }

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      // The template lives in a CSS variable so the single-column mobile layout
      // stays a plain class and only `lg` picks up the resizable columns.
      className={cn('grid grid-cols-1 lg:[grid-template-columns:var(--split-cols)]', className)}
      style={{ '--split-cols': `${String(leftPct)}% 0px 1fr` } as React.CSSProperties}
    >
      {left}

      {/* Divider. A slider (not a button) is the right widget for a splitter: it
          reports its position via aria-value* and resizes with the arrow keys. */}
      <div
        role="slider"
        aria-orientation="vertical"
        aria-valuenow={Math.round(leftPct)}
        aria-valuemin={minLeftPct}
        aria-valuemax={maxLeftPct}
        aria-label="Resize panels"
        data-testid={`${testId}-divider`}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        onDoubleClick={() => {
          setLeftPct(defaultLeftPct);
        }}
        title="Drag to resize — double-click to reset"
        className="group relative z-20 hidden cursor-col-resize touch-none focus-visible:outline-none lg:block"
      >
        {/* Wider invisible hit area than the 1px visual line. */}
        <span className="absolute inset-y-0 -left-2 w-4" />
        <span className="group-hover:bg-primary group-focus-visible:bg-primary absolute inset-y-0 -left-px w-px bg-transparent transition-colors" />
      </div>

      {right}
    </div>
  );
}
